/**
 * NoteCard — a single kind-1 note row with:
 *  - Avatar + display name + timestamp
 *  - Rendered note content (NoteContent)
 *  - Reply / React / Delete actions
 *  - Inline reply composer (collapsible)
 *  - Edit modal (publish new note, delete old)
 *  - "View thread" affordance that expands replies inline (NoteThread)
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import type { Event as NostrToolsEvent } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useInvalidateNotes, useNoteReplies } from '@/hooks/useNoteFeed';
import { useToast } from '@/hooks/useToast';
import { NoteContent } from '@/components/NoteContent';
import { NoteComposer } from '@/components/NoteComposer';
import { ZapDialog } from '@/components/ZapDialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/* ── helpers ─────────────────────────────────────────────────────────────── */

function timeAgo(ts: number): string {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(ts * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/* ── Action button ────────────────────────────────────────────────────────── */

function ActionBtn({
  icon,
  label,
  count,
  active,
  onClick,
  style,
}: {
  icon: React.ReactNode;
  label: string;
  count?: number;
  active?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-colors hover:bg-black/8 dark:hover:bg-white/8"
      style={{ color: active ? 'var(--gb-accent)' : 'var(--text-secondary)', ...style }}
    >
      {icon}
      {count !== undefined && count > 0 && <span>{count}</span>}
    </button>
  );
}

/* ── Icons ─────────────────────────────────────────────────────────────────── */

function ReplyIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function HeartIcon({ filled }: { filled?: boolean }) {
  return filled ? (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );
}

function ZapIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M7 2v11h3v9l7-12h-4l4-8z" />
    </svg>
  );
}

function ChevronDownIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/* ── NoteCard ─────────────────────────────────────────────────────────────── */

interface NoteCardProps {
  event: NostrEvent;
  /** Depth 0 = top-level, 1+ = nested reply. Controls indentation and visual treatment. */
  depth?: number;
  /** When true, the reply thread is already open (e.g. in NoteThread view) */
  threadOpen?: boolean;
  /** Hide the "view replies" toggle (already inside a thread view) */
  hideRepliesToggle?: boolean;
  onDeleted?: (id: string) => void;
}

export function NoteCard({
  event,
  depth = 0,
  threadOpen: externalThreadOpen,
  hideRepliesToggle = false,
  onDeleted,
}: NoteCardProps) {
  const { user } = useCurrentUser();
  const { mutate: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { toast } = useToast();
  const invalidateNotes = useInvalidateNotes();

  const author = useAuthor(event.pubkey);
  const meta = author.data?.metadata;
  const displayName = meta?.name ?? meta?.display_name ?? event.pubkey.slice(0, 8) + '…';

  // Profile event used as zap target (kind 0)
  const profileEvent = author.data?.event as NostrToolsEvent | undefined;
  const canZap = !!user &&
    user.pubkey !== event.pubkey &&
    !!profileEvent &&
    !!(meta?.lud16 || meta?.lud06);

  const [replyOpen, setReplyOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [threadOpen, setThreadOpen] = useState(externalThreadOpen ?? false);
  const [liked, setLiked] = useState(false);

  const isOwn = user?.pubkey === event.pubkey;

  // Only fetch replies count when depth === 0 and not hidden
  const { data: replies } = useNoteReplies(hideRepliesToggle ? '' : event.id);
  const replyCount = replies?.length ?? 0;

  const handleLike = () => {
    if (!user) {
      toast({ title: 'Log in to react', variant: 'destructive' });
      return;
    }
    if (liked) return;
    setLiked(true);
    publishEvent(
      { kind: 7, content: '🤙', tags: [['e', event.id], ['p', event.pubkey]] },
      {
        onSuccess: () => toast({ title: 'Reacted!' }),
        onError: () => setLiked(false),
      },
    );
  };

  const handleDelete = () => {
    publishEvent(
      {
        kind: 5,
        content: '',
        tags: [['e', event.id], ['k', '1']],
      },
      {
        onSuccess: () => {
          invalidateNotes(event.pubkey);
          onDeleted?.(event.id);
          toast({ title: 'Note deleted' });
        },
        onError: (err) => {
          toast({ title: 'Delete failed', description: String(err), variant: 'destructive' });
        },
      },
    );
  };

  const indent = depth > 0;

  return (
    <>
      <div
        className={indent ? 'pl-4' : ''}
        style={indent ? { borderLeft: '2px solid var(--gb-border)', marginLeft: 20 } : undefined}
      >
        <div
          className="px-4 py-3 transition-colors"
          style={{ borderBottom: depth === 0 ? '1px solid var(--gb-border)' : undefined }}
        >
          {/* Author row */}
          <div className="flex items-start gap-3">
            <Link to={`/profile/${event.pubkey}`} className="shrink-0">
              <Avatar className="h-9 w-9">
                <AvatarImage src={meta?.picture} />
                <AvatarFallback
                  className="text-xs font-bold"
                  style={{ background: 'var(--gb-accent)', color: '#fff' }}
                >
                  {displayName[0]?.toUpperCase() ?? '?'}
                </AvatarFallback>
              </Avatar>
            </Link>

            <div className="flex-1 min-w-0">
              {/* Name + time */}
              <div className="flex items-baseline gap-2 flex-wrap">
                <Link
                  to={`/profile/${event.pubkey}`}
                  className="text-sm font-semibold no-underline hover:underline truncate"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {displayName}
                </Link>
                <span className="text-xs shrink-0" style={{ color: 'var(--text-secondary)' }}>
                  {timeAgo(event.created_at)}
                </span>
              </div>

              {/* Content */}
              <div className="mt-1 mb-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                <NoteContent event={event} className="text-sm leading-relaxed" />
              </div>

              {/* Action bar */}
              <div className="flex items-center gap-0.5 -ml-2">
                {/* Reply */}
                <ActionBtn
                  icon={<ReplyIcon />}
                  label="Reply"
                  count={!hideRepliesToggle ? replyCount : undefined}
                  onClick={() => {
                    if (!user) {
                      toast({ title: 'Log in to reply', variant: 'destructive' });
                      return;
                    }
                    setReplyOpen(v => !v);
                  }}
                />

                {/* Like */}
                <ActionBtn
                  icon={<HeartIcon filled={liked} />}
                  label="React"
                  active={liked}
                  onClick={handleLike}
                />

                {/* Zap author */}
                {canZap && profileEvent && (
                  <ZapDialog target={profileEvent}>
                    <button
                      type="button"
                      aria-label={`Zap ${displayName}`}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-colors hover:bg-black/8 dark:hover:bg-white/8"
                      style={{ color: '#f7931a' }}
                    >
                      <ZapIcon />
                      <span>Zap</span>
                    </button>
                  </ZapDialog>
                )}

                {/* View thread toggle */}
                {!hideRepliesToggle && replyCount > 0 && (
                  <ActionBtn
                    icon={<ChevronDownIcon open={threadOpen} />}
                    label={threadOpen ? 'Collapse replies' : `View ${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
                    count={undefined}
                    onClick={() => setThreadOpen(v => !v)}
                    style={{ fontSize: 11 }}
                  />
                )}

                {/* Owner actions */}
                {isOwn && (
                  <>
                    <ActionBtn
                      icon={<EditIcon />}
                      label="Edit"
                      onClick={() => setEditOpen(true)}
                    />
                    <ActionBtn
                      icon={<TrashIcon />}
                      label="Delete"
                      onClick={() => setDeleteOpen(true)}
                      style={{ color: '#f44336' }}
                    />
                  </>
                )}
              </div>

              {/* Inline reply composer */}
              {replyOpen && (
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--gb-border)' }}>
                  <NoteComposer
                    replyTo={event}
                    autoFocus
                    onPublished={() => {
                      setReplyOpen(false);
                      setThreadOpen(true);
                    }}
                    onCancel={() => setReplyOpen(false)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Inline thread expansion */}
        {threadOpen && !hideRepliesToggle && (
          <NoteThread
            parentEvent={event}
            replies={replies ?? []}
            depth={depth + 1}
          />
        )}
      </div>

      {/* Edit dialog */}
      {editOpen && (
        <NoteEditDialog
          event={event}
          onClose={() => setEditOpen(false)}
          onDeleted={() => {
            setEditOpen(false);
            onDeleted?.(event.id);
          }}
        />
      )}

      {/* Delete confirm */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent
          style={{ background: 'var(--bg-secondary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'var(--text-primary)' }}>Delete note?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'var(--text-secondary)' }}>
              This publishes a NIP-09 deletion request. The note will be removed from this feed
              immediately. Note that some relays may retain a copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isPublishing}
              style={{ background: '#f44336', color: '#fff', border: 'none' }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ── NoteThread ───────────────────────────────────────────────────────────── */

/**
 * Renders a flat list of reply events as indented NoteCards.
 * Each reply can itself be expanded to see its own replies (recursive).
 */
interface NoteThreadProps {
  parentEvent: NostrEvent;
  replies: NostrEvent[];
  depth?: number;
}

export function NoteThread({ parentEvent, replies, depth = 1 }: NoteThreadProps) {
  if (replies.length === 0) return null;

  // Filter to direct replies to parentEvent
  const directReplies = replies.filter(r =>
    r.tags.some(([n, id, , marker]) => {
      if (n !== 'e') return false;
      // NIP-10: prefer marker
      if (marker === 'reply') return id === parentEvent.id;
      if (marker === 'root') return false;
      // No marker: treat last 'e' tag as reply target
      const eTags = r.tags.filter(([t]) => t === 'e');
      return eTags[eTags.length - 1]?.[1] === parentEvent.id;
    }),
  );

  return (
    <div>
      {directReplies.map(reply => (
        <NoteCard
          key={reply.id}
          event={reply}
          depth={depth}
          hideRepliesToggle={false}
        />
      ))}
    </div>
  );
}

/* ── NoteEditDialog ───────────────────────────────────────────────────────── */

/**
 * Inline edit dialog: shows a composer pre-filled with the note content.
 * On submit it publishes a new note and requests deletion of the old one.
 */
interface NoteEditDialogProps {
  event: NostrEvent;
  onClose: () => void;
  onDeleted: () => void;
}

function NoteEditDialog({ event, onClose, onDeleted }: NoteEditDialogProps) {
  const { user } = useCurrentUser();
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const { toast } = useToast();
  const invalidateNotes = useInvalidateNotes();
  const [content, setContent] = useState(event.content);

  if (!user) return null;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed || trimmed === event.content.trim()) {
      onClose();
      return;
    }

    // Preserve any reply tags from the original
    const replyTags = event.tags.filter(([n]) => n === 'e' || n === 'p');

    // Publish updated note
    publishEvent(
      { kind: 1, content: trimmed, tags: replyTags },
      {
        onSuccess: () => {
          // Delete the old note
          publishEvent(
            { kind: 5, content: '', tags: [['e', event.id], ['k', '1']] },
            {
              onSuccess: () => {
                invalidateNotes(event.pubkey);
                onDeleted();
                toast({ title: 'Note updated!' });
              },
            },
          );
        },
        onError: (err) => {
          toast({ title: 'Update failed', description: String(err), variant: 'destructive' });
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl p-5 shadow-2xl"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--gb-border)' }}
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-base font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Edit note
        </h2>
        <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
          Editing publishes a new note and requests deletion of the original. Some relays may retain the old copy.
        </p>
        <form onSubmit={handleSave} className="space-y-3">
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={5}
            className="w-full rounded-xl text-sm p-3 resize-none focus:outline-none"
            style={{
              background: 'var(--bg-primary)',
              border: '1px solid var(--gb-border)',
              color: 'var(--text-primary)',
            }}
            autoFocus
            disabled={isPending}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!content.trim() || isPending}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'var(--gb-accent)', opacity: (!content.trim() || isPending) ? 0.5 : 1 }}
            >
              {isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── NoteCardSkeleton ─────────────────────────────────────────────────────── */

import { Skeleton } from '@/components/ui/skeleton';

export function NoteCardSkeleton() {
  return (
    <div
      className="flex gap-3 px-4 py-3"
      style={{ borderBottom: '1px solid var(--gb-border)' }}
    >
      <Skeleton className="h-9 w-9 rounded-full shrink-0" style={{ background: 'var(--bg-secondary)' }} />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" style={{ background: 'var(--bg-secondary)' }} />
        <Skeleton className="h-4 w-full" style={{ background: 'var(--bg-secondary)' }} />
        <Skeleton className="h-4 w-4/5" style={{ background: 'var(--bg-secondary)' }} />
        <Skeleton className="h-3 w-24 mt-1" style={{ background: 'var(--bg-secondary)' }} />
      </div>
    </div>
  );
}
